<script setup lang="ts">
import axios from 'axios';
import PlayNarrationButton from '@/components/base/PlayNarrationButton.vue';
import QuizQuestionAnswer from '@/components/quiz/QuizQuestionAnswer.vue';
import { computed, onMounted } from 'vue';
import useAudioStore from '@/store/audio';
import config from '@/config/ConfigProvider';
import type { CloseEndedQuestion } from '@ilteducation/exercise-service-types';
import { useLocale } from '@/plugins/translation';
import { AudioResource } from '@ilteducation/videos';
import { storeToRefs } from 'pinia';
import useQuizStore from '@/store/quiz';

const props = defineProps<{ content: CloseEndedQuestion; contentIndex: number }>();

const quizStore = useQuizStore();
const { mode } = storeToRefs(quizStore);
const { addAudioResources } = useAudioStore();
const { audioResources } = storeToRefs(useAudioStore());
const { t, l } = useLocale();

const answerMode = computed(() => mode.value === 'quiz-results');
const phraseAudio = computed(() => audioResources.value.find((el: AudioResource) => el.key === props.content.phrase));
const playAllData = computed(() => {
  const answers: AudioResource[] = [];
  props.content.options.forEach((el) => {
    const target = audioResources.value.find(
      (audio: AudioResource) => audio.key.toLowerCase() === el.value.toLowerCase(),
    );
    if (target) answers.push(target);
  });
  const resultData: AudioResource[] = [];
  if (phraseAudio.value) {
    resultData.push(phraseAudio.value);
  }
  const allAnswersExist = answers.length === props.content.options.length;
  if (allAnswersExist) {
    answers.forEach((item) => {
      resultData.push(item);
    });
  }
  if (phraseAudio.value && allAnswersExist) {
    return resultData;
  }
  return null;
});
const getAudioFiles = (items: { id: string; key: string; language: string }[]) => {
  items.forEach((el) => {
    if (el.id) {
      addAudioResources({
        url: '',
        key: el.key,
        id: el.id,
        lang: el.language === 'swe' ? 'sv' : el.language,
      });
    }
  });
};
const getAnswerUrl = (key: string) =>
  audioResources.value.find((item: AudioResource) => item.key.toLowerCase() === key.toLowerCase());
const getAudio = () => {
  if (!props.content.phrase) {
    return;
  }
  axios
    .get(
      `${config.value('audioCentralUrl')}?ref=${encodeURIComponent(props.content.phrase)}&language=swe&recType=voice`,
    )
    .then((res) => {
      if (res.data && res.data[0] && res.data[0].id) {
        addAudioResources({
          url: '',
          key: props.content.phrase,
          id: res.data[0].id,
          lang: res.data[0].language === 'swe' ? 'sv' : res.data[0].language,
        });
      }
    });
};
const getAnswersAudio = () => {
  const answers: string[] = [];
  props.content.options.forEach((el) => {
    if (
      !audioResources.value.find((audio: AudioResource) => audio.key.toLowerCase() === el.value.toLowerCase()) &&
      el.value
    ) {
      answers.push(`ref=${encodeURIComponent(el.value)}`);
    }
  });
  if (answers.length) {
    axios
      .get(`${config.value('audioCentralUrl')}?${answers.join('&')}&language=swe&recType=voice`)
      .then((res) => getAudioFiles(res.data));
  }
};
onMounted(() => {
  if (!phraseAudio.value) {
    getAudio();
  }
  getAnswersAudio();
  if (props.contentIndex !== 0 && !answerMode.value) {
    const quizQuestion = document.getElementById('quiz-question');
    if (quizQuestion) {
      quizQuestion.focus();
    }
  }
});
</script>
<template>
  <div class="quiz-header mt-2">
    <div class="d-flex align-start justify-space-between flex-nowrap ga-3">
      <div class="d-flex align-center ga-2">
        <span id="quiz-question" class="question" tabindex="-1">{{ content.phrase }}</span>
        <PlayNarrationButton v-if="phraseAudio" :value="[phraseAudio]" />
      </div>
      <PlayNarrationButton
        v-if="playAllData"
        class="flex-shrink-0"
        :button-styled="true"
        :button-text="t(l.film.play_all)"
        :value="playAllData"
      />
    </div>

    <div class="quiz-questions-container py-8 mt-2" :class="{ 'nopad-top': answerMode }">
      <div class="px-0">
        <QuizQuestionAnswer
          v-for="(option, index) in content.options"
          :key="index"
          :content-index="contentIndex"
          :option="option"
          :correct-option="content.answer"
          :answer-audio="getAnswerUrl(option.value)"
        >
        </QuizQuestionAnswer>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.quiz-questions-container {
  min-width: 100%;
}
.question {
  font-family: 'Proxima-Soft', Helvetica, Arial, 'Lucida Grande', sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 22px;
  color: rgb(var(--v-theme-on-surface));
}
</style>
